import { createSelector } from "reselect";

import * as R from "ramda";

import { getters } from "Portal/CallForPapersForm/model";

import { user as getUser } from "redux/modules/user/selectors";

import {
  CONTACT_COLORS,
  GROUP_COLORS,
  LOGIN_COLORS,
  WELCOME_COLORS
} from "Portal/CallForPapersForm/constants";

import isValid from "utils/value-types/validations/is-valid";

import isEmpty from "utils/value-types/is-empty";

import Helpers from "utils/Global/Helpers";

import * as STANDARD_MODULE_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";

import { getFontCssName, resolveFontCss } from "utils/resolve-font-css";

import { isEmptyOrNil, getOptimizedUrl } from "utils/General";

export const getEnableAdditionalContacts = createSelector(
  getters.enableAdditionalContacts,
  R.identity
);

export const getIsEditing = createSelector(
  getters.submissionId,
  submissionId => {
    return Boolean(submissionId);
  }
);

export const getWelcomeColor = createSelector(
  getters.activeIndex,
  activeIndex => WELCOME_COLORS[activeIndex]
);

export const getWelcomeData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : R.find(s => s.id === "welcome", formData.sections)
);

export const getLoginColor = createSelector(
  getters.activeIndex,
  activeIndex => LOGIN_COLORS[activeIndex]
);

export const getGroupColor = createSelector(
  getters.activeIndex,
  activeIndex => GROUP_COLORS[activeIndex]
);

export const getGroupData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : R.find(s => s.id === "group", formData.sections)
);

export const getPageTitle = createSelector(getters.formData, R.prop("title"));

const isRequiredFieldValid = (
  value,
  emailId,
  _,
  wannaCompleteAndIsInvalid = false
) => {
  if (R.isEmpty(R.dissoc("id", value)) && !wannaCompleteAndIsInvalid) {
    return true;
  }
  const type = R.propOr("text", "type", value);
  if (value?.id === emailId) {
    return Helpers.isValidEmail(R.propOr("", "value", value));
  }

  if (typeof value?.value !== "object") {
    return isValid(value, type);
  }

  return isValid(value, type);
};

const isFieldValid = (value, emailId) => {
  if (isEmpty(value, value.type)) {
    return true;
  }

  return isRequiredFieldValid(value, emailId);
};

export const getGroupDataFields = createSelector(
  getGroupData,
  getters.groupValues,
  getters.selectedAccountId,
  getters.isClosed,
  getters.isViewOnly,
  (groupData, groupValues, isGroupSelected, isClosed, isViewOnly) =>
    R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, groupValues);

        return {
          ...field,
          disabled:
            isClosed || isViewOnly
              ? true
              : field.id === STANDARD_MODULE_FIELD_IDS.ACCOUNTS.NAME
              ? Boolean(isGroupSelected)
              : false,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.ACCOUNTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.ACCOUNTS.EMAIL
              )
        };
      }),
      R.propOr([], "fields")
    )(groupData)
);

export const getContactColor = createSelector(
  getters.activeIndex,
  activeIndex => CONTACT_COLORS[activeIndex]
);

export const getContactData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : R.find(s => s.id === "contact", formData.sections)
);

export const getSelectedSpeakerContactValues = createSelector(
  getters.selectedSpeakerId,
  getters.speakers,
  (selectedSpeakerId, speakers) => {
    return R.compose(
      R.prop("contactValues"),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);
  }
);

export const getSelectedSpeakerIsExternal = createSelector(
  getters.selectedSpeakerId,
  getters.speakers,
  getters.eventEnableUniqueContacts,
  (selectedSpeakerId, speakers, eventEnableUniqueContacts) => {
    if (!eventEnableUniqueContacts) {
      return false;
    }
    return R.compose(
      R.propOr(false, "is_external"),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);
  }
);

export const getSelectedSpeakerAdditionalContacts = createSelector(
  getters.selectedSpeakerId,
  getters.speakers,
  (selectedSpeakerId, speakers) => {
    return R.compose(
      R.propOr([], "additionalContacts"),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);
  }
);

export const getSelectedSpeakerEmailError = createSelector(
  getters.selectedSpeakerId,
  getters.speakers,
  (selectedSpeakerId, speakers) => {
    return R.compose(
      R.prop("emailError"),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);
  }
);

export const getContactDataFields = createSelector(
  getContactData,
  getters.contactValues,
  getters.selectedContactId,
  getters.isClosed,
  getters.speakers,
  getters.eventEnableUniqueContacts,
  (
    contactData,
    contactValues,
    selectedContactId,
    isClosed,
    speakers,
    eventEnableUniqueContacts
  ) => {
    const selectedSpeaker = speakers.find(s => s.id === selectedContactId);
    const isSpeakerExternal =
      eventEnableUniqueContacts &&
      selectedSpeaker &&
      selectedSpeaker.is_external
        ? true
        : false;

    return R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, contactValues);
        return {
          ...field,
          disabled:
            isSpeakerExternal || isClosed
              ? true
              : [
                  (STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
                  STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME)
                ].includes(field.id)
              ? Boolean(selectedContactId)
              : false,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
                contactValues
              )
        };
      }),
      R.propOr([], "fields")
    )(contactData);
  }
);

export const getSpeakerContactDataFields = createSelector(
  getContactData,
  getSelectedSpeakerContactValues,
  getters.selectedContactId,
  getters.isClosed,
  getters.wannaCompleteAndIsInvalid,
  getters.speakers,
  getters.selectedSpeakerId,
  getters.eventEnableUniqueContacts,
  (
    contactData,
    contactValues,
    selectedContactId,
    isClosed,
    wannaCompleteAndIsInvalid,
    speakers,
    selectedSpeakerId,
    eventEnableUniqueContacts
  ) => {
    const selectedSpeaker = speakers.find(s => s.id === selectedSpeakerId);
    const isSpeakerExternal =
      eventEnableUniqueContacts &&
      selectedSpeaker &&
      selectedSpeaker.is_external
        ? true
        : false;

    return R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, contactValues);
        const isExternalSpeaker =
          eventEnableUniqueContacts && Boolean(isSpeakerExternal);

        return {
          ...field,
          showObfuscatedValue:
            isExternalSpeaker &&
            ![
              STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
              STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
              STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
            ].includes(field.id)
              ? true
              : false,
          disabled:
            isExternalSpeaker || isClosed
              ? true
              : [
                  (STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
                  STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME)
                ].includes(field.id)
              ? Boolean(selectedContactId)
              : false,
          isValid: isExternalSpeaker
            ? true
            : !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
                contactValues,
                wannaCompleteAndIsInvalid
              )
        };
      }),
      R.filter(field => field.id !== "is-speaker"),
      R.propOr([], "fields")
    )(contactData);
  }
);

export const getSpeakerContactDataFieldsFiltered = createSelector(
  getSpeakerContactDataFields,
  R.filter(
    ({ id }) =>
      ![
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
  )
);

export const getContactFirstLastNameFields = createSelector(
  getContactDataFields,
  R.compose(
    R.sortBy(({ id }) =>
      R.prop(id)({
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME]: 0,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME]: 1,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: 2
      })
    ),
    R.filter(({ id }) =>
      [
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
    )
  )
);

export const getSpeakerContactFirstLastNameFields = createSelector(
  getSpeakerContactDataFields,
  R.compose(
    R.sortBy(({ id }) =>
      R.prop(id)({
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME]: 0,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME]: 1,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: 2
      })
    ),
    R.filter(({ id }) =>
      [
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
    )
  )
);

export const getNextButtonText = createSelector(
  getters.activeIndex,
  activeIndex => {
    if (activeIndex === 0) {
      return "Continue";
    } else if (activeIndex === 1) {
      return "Next Step";
    } else if (activeIndex === 2) {
      return "Next Step";
    }
    return "Complete";
  }
);

export const applyRules = (field, fields, values) => {
  const allRulesResults = R.map(rule => {
    const operator = R.prop("operator", rule);
    const conditionField = R.find(
      f => R.prop("form_field_id", f) === R.prop("condition_field_id", rule),
      fields
    );
    const conditionType = R.prop("type", conditionField);

    let actualConditionValue;
    if (conditionType === "dropdown") {
      actualConditionValue = R.pathOr(
        [],
        [conditionField.id, "value", "records"],
        values
      );
    } else {
      actualConditionValue = [R.pathOr([], [conditionField.id], values)];
    }
    const ruleValues = R.pathOr([], ["condition", "values"], rule);

    const ruleValuesResults = R.map(v => {
      const valExists = R.find(av => {
        const condVal = R.prop("value", av) || false;
        const condId = R.prop("id", av);
        const ruleVal =
          conditionType === "checkbox"
            ? JSON.parse(R.prop("value", v))
            : R.prop("value", v);

        return condVal === ruleVal || condId === ruleVal;
      }, actualConditionValue);

      // check if we have transitive conditional logic
      const isExitsTranstiveConditionalField = applyRules(
        conditionField,
        fields,
        values
      );

      // first time  + user select undefined value on the dropdown
      if (
        (valExists === undefined && R.length(actualConditionValue) === 0) ||
        (valExists === undefined && R.isNil(actualConditionValue[0])) ||
        !isExitsTranstiveConditionalField
      ) {
        return false;
      }
      if (operator === "IS" && !valExists) {
        return false;
      } else return !(operator === "IS_NOT" && valExists);
    }, ruleValues);

    return ruleValuesResults.includes(true);
  }, R.propOr([], "rules", field));
  return R.length(allRulesResults) > 0 ? allRulesResults.includes(true) : true;
};

export const getGroupValuesInValid = createSelector(
  getGroupData,
  getters.groupValues,
  (groupData, groupValues) => {
    const fields = R.propOr([], "fields", groupData);

    if (R.isEmpty(fields)) {
      return true;
    }

    return R.compose(
      R.filter(field => {
        const renderField = applyRules(
          R.find(({ id }) => field.id === id, fields),
          fields,
          groupValues
        );

        if (!renderField) {
          return false;
        }
        if (!field.required) {
          if (R.isEmpty(R.dissoc("id", field)) || isEmpty(field, field.type)) {
            return false;
          }
        }
        const type = R.propOr("text", "type", field);
        if (type === "boolean") {
          return !isValid(field, "checkbox");
        }
        if (field.id === STANDARD_MODULE_FIELD_IDS.ACCOUNTS.EMAIL) {
          return !Helpers.isValidEmail(R.propOr("", "value", field));
        }
        return !isValid(field, type);
      }),
      R.map(({ id, required }) => ({
        id,
        ...R.propOr({}, id, groupValues),
        required
      }))
    )(fields);
  }
);

export const getSpeakerContactValuesInvalid = createSelector(
  getSpeakerContactDataFields,
  getters.speakers,
  getEnableAdditionalContacts,
  getters.eventEnableUniqueContacts,
  (fields, speakers, enableAdditionalContacts, eventEnableUniqueContacts) => {
    if (R.isEmpty(fields)) {
      return true;
    }

    return R.reduce(
      (speakers, speaker) => {
        const isSpeakerExternal =
          eventEnableUniqueContacts && speaker.is_external ? true : false;

        const contactValues = speaker.contactValues;

        const fieldsInvalid = isSpeakerExternal
          ? []
          : R.compose(
              R.values,
              R.filter(field => {
                const renderField = applyRules(
                  R.find(({ id }) => field.id === id, fields),
                  fields,
                  contactValues
                );

                if (!renderField) {
                  return false;
                }

                if (!field.required) {
                  if (
                    R.isEmpty(R.dissoc("id", field)) ||
                    isEmpty(field, field.type)
                  ) {
                    return false;
                  }
                }
                const type = R.propOr("text", "type", field);
                if (field.id === STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL) {
                  return !Helpers.isValidEmail(R.propOr("", "value", field));
                }
                return !isValid(field, type);
              }),
              R.map(({ id, required }) => ({
                id,
                ...R.propOr({}, id, contactValues),
                required
              }))
            )(fields);

        const additionalContacts =
          enableAdditionalContacts && speaker.additionalContacts
            ? speaker.additionalContacts
            : [];

        const invalidAdditionalContacts = R.filter(
          contact =>
            R.any(
              prop =>
                R.isEmpty(R.prop(prop, contact)) ||
                R.isEmpty(R.propOr("", prop, contact)),
              ["firstName", "lastName", "email"]
            ) || !Helpers.isValidEmail(R.propOr("", "email", contact)),
          additionalContacts
        );

        if (
          R.length(fieldsInvalid) > 0 ||
          R.length(invalidAdditionalContacts) > 0
        ) {
          return [
            ...speakers,
            {
              id: speaker.id,
              fields: fieldsInvalid.concat(invalidAdditionalContacts)
            }
          ];
        } else {
          return speakers;
        }
      },
      [],
      speakers
    );
  }
);

export const getLoginValuesValid = createSelector(
  getters.loginValues,
  getters.userExists,
  getters.isPasswordValid,
  getters.userFirstName,
  getters.userLastName,
  getters.termsAccepted,
  (loginValues, userExists, isPasswordValid, fname, lname, termsAccepted) => {
    if (
      R.isEmpty(loginValues.email) ||
      isEmpty(loginValues.email, "email") ||
      !isValid(loginValues.email, "email")
    ) {
      return false;
    }

    if (
      R.isEmpty(loginValues.password) ||
      isEmpty(loginValues.password, "text") ||
      !isValid(loginValues.password, "text")
    ) {
      return false;
    }

    if (!userExists) {
      if (
        !fname ||
        !fname.length ||
        !lname ||
        !lname.length ||
        !termsAccepted
      ) {
        return false;
      }
    }

    return !(!userExists && !isPasswordValid);
  }
);

export const getLoginData = createSelector(
  getters.loginValues,
  R.map(data => R.propOr("", "value", data))
);

export const getGroupValues = createSelector(
  getters.groupValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getContactValues = createSelector(
  getters.contactValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getSpeakerContactValues = createSelector(
  getters.speakers,
  R.map(speaker => ({
    ...speaker,
    contactValues: R.filter(data => !R.isEmpty(R.propOr("", "value", data)))(
      speaker.contactValues
    )
  }))
);

export const getSelectedSpeakerEmailAddress = createSelector(
  getters.speakers,
  getters.selectedSpeakerId,
  (speakers, selectedSpeakerId) => {
    const email = R.compose(
      R.pathOr(null, [
        "contactValues",
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
        "value"
      ]),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);

    return email && email.length ? email.toLowerCase().trim() : "";
  }
);

export const getSpeakerEmailErrorsClean = createSelector(
  getters.speakers,
  speakers =>
    speakers.reduce((acc, speaker) => acc && !speaker.emailError?.length, true)
);

export const getSubmissionLimit = createSelector(
  getters.enableEventSubmissionLimit,
  getters.enableSubmissionLimit,
  getters.eventSubmissionLimit,
  getters.submissionLimit,
  (
    enableEventSubmissionLimit,
    enableFormSubmissionLimit,
    eventSubmissionLimit,
    formSubmissionLimit
  ) => {
    let submissionLimit = 0;

    // handle: set default to event submission limit, if set
    if (enableEventSubmissionLimit && eventSubmissionLimit) {
      submissionLimit = eventSubmissionLimit;
    }

    // handle: form submission limit should always be shown, if set
    if (enableFormSubmissionLimit && formSubmissionLimit) {
      submissionLimit = formSubmissionLimit;
    }
    return submissionLimit;
  }
);

export const getNextButtonDisabled = createSelector(
  getters.activeIndex,
  getters.isClosed,
  getters.isViewOnly,
  getters.userCanSubmitNewSubmissions,
  getters.verifyingEmail,
  getIsEditing,
  (
    activeIndex,
    isClosed,
    isViewOnly,
    userCanSubmitNewSubmissions,
    verifyingEmail,
    isEditing
  ) => {
    if (verifyingEmail) return true;
    if (isViewOnly) return true;
    if (activeIndex === 0) {
      return false;
    } else if (activeIndex === 1) {
      return (!isEditing && !userCanSubmitNewSubmissions) || isClosed;
    } else if (activeIndex === 2) {
      return (!isEditing && !userCanSubmitNewSubmissions) || isClosed;
    } else if (activeIndex === 3) {
      return (!isEditing && !userCanSubmitNewSubmissions) || isClosed;
    }
    return false;
  }
);

export const getShowBackButton = createSelector(
  getters.activeIndex,
  activeIndex => activeIndex > 0
);

export const getPortalUrl = createSelector(
  getters.formData,
  data => `/portals/${data.event_slug}/${data.event_uuid}`
);

export const getUserName = createSelector(getUser, user => {
  const name = [user.fname, user.lname].filter(v => v).join(" ");
  if (name.length) {
    return name;
  }
  return user.email;
});

export const getAvailableAccounts = createSelector(
  getters.eventUserGroups,
  accounts => {
    return accounts && accounts.length
      ? [
          ...accounts.map(a => ({
            label: a.name,
            value: a.id
          })),
          {
            value: null,
            label: "Fill out as a new group..."
          }
        ]
      : [];
  }
);

export const getAvailableContacts = createSelector(
  getters.selectedAccountId,
  getters.eventUserGroups,
  (selectedAccountId, accounts) => {
    if (!selectedAccountId) {
      return [];
    }
    const contacts = R.compose(
      R.propOr([], "contacts"),
      R.find(a => a.id === selectedAccountId)
    )(accounts);

    return contacts && contacts.length
      ? [
          ...contacts.map(a => ({
            label: a.name,
            value: a.id
          })),
          {
            value: null,
            label: "Fill out as a new person..."
          }
        ]
      : [];
  }
);

const getIsValidTabSpeaker = (
  contactValues = [],
  fields = [],
  additionalContacts = [],
  enableAdditionalContacts
) => {
  const areContactValuesValid = R.compose(
    R.all(field =>
      isRequiredFieldValid(field, STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL)
    ),
    R.filter(R.propEq("required", true)), // filter required fields
    R.map(({ id, required }) => ({
      id,
      ...R.propOr({}, id, contactValues),
      required
    }))
  )(fields);
  const areAdditionalContactsValid =
    enableAdditionalContacts && additionalContacts.length
      ? additionalContacts.reduce(
          (acc, contact) =>
            acc &&
            contact.firstName.length &&
            contact.lastName.length &&
            Helpers.isValidEmail(contact.email),
          true
        )
      : true;
  return areContactValuesValid && areAdditionalContactsValid;
};

export const getSpeakerTabs = createSelector(
  getters.speakers,
  getters.isClosed,
  getSpeakerContactDataFields,
  getters.speakersLimit,
  getEnableAdditionalContacts,
  (speakers, isClosed, fields, speakersLimit, enableAdditionalContacts) => {
    const lengthSpeakers = R.length(speakers);
    const tabs = speakers.map((speaker, idx) => {
      const fname = R.path([
        "contactValues",
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        "value"
      ])(speaker);
      return {
        id: speaker.id,
        title: fname ? fname : `Person ${idx + 1}`,
        isValid:
          lengthSpeakers > 1
            ? getIsValidTabSpeaker(
                speaker.contactValues,
                fields,
                speaker.additionalContacts,
                enableAdditionalContacts
              )
            : true
      };
    });
    if (!isClosed && tabs.length < speakersLimit) {
      tabs.push({
        id: "add",
        title: "Add +",
        isValid: true
      });
    }

    return tabs;
  }
);

export const allowRemovePerson = createSelector(
  getters.speakers,
  getters.isClosed,
  getters.selectedSpeakerId,
  (speakers, isClosed, selectedSpeakerId) => {
    const idx = speakers.findIndex(s => s.id === selectedSpeakerId);

    return isClosed
      ? false
      : idx === 0
      ? false
      : speakers && speakers.length > 1;
  }
);

export const showThisIsYou = createSelector(
  getters.speakers,
  getters.selectedSpeakerId,
  (speakers, selectedSpeakerId) => {
    const idx = speakers.findIndex(s => s.id === selectedSpeakerId);
    return idx === 0;
  }
);

export const getNavigationTitles = createSelector(
  getters.sections,
  sections => {
    if (R.length(sections) > 0) {
      const [welcome, ...rest] = R.map(R.prop("navigation_title"), sections);
      return [welcome, "Login", ...rest];
    } else {
      return ["Welcome", "Login", "Session Info", "Speaker Info"];
    }
  }
);

/**
 * Styles
 */

export const getLogoImageUrl = createSelector(
  getters.styles,
  R.pipe(
    R.prop("logo_image_url"),
    R.ifElse(isEmptyOrNil, R.always(null), url =>
      getOptimizedUrl({ url, width: 135, height: 135, quality: 70 })
    )
  )
);
export const getBackgroundImageUrl = createSelector(
  getters.styles,
  R.pipe(
    R.prop("background_image_url"),
    R.ifElse(isEmptyOrNil, R.always(null), url =>
      getOptimizedUrl({ url, quality: 70 })
    )
  )
);
export const getBackgroundImageUrlBig = createSelector(
  getters.styles,
  R.pipe(
    R.prop("background_image_url"),
    R.ifElse(isEmptyOrNil, R.always(null), url =>
      getOptimizedUrl({ url, width: 1024, height: 440, quality: 80 })
    )
  )
);
export const getBackgroundImageUrlSmall = createSelector(
  getters.styles,
  R.pipe(
    R.prop("background_image_url"),
    R.ifElse(isEmptyOrNil, R.always(null), url =>
      getOptimizedUrl({ url, width: 480, height: 330, quality: 80 })
    )
  )
);
export const getLogoImageAltText = createSelector(
  getters.styles,
  R.prop("logo_alt_text")
);
export const getBackgroundImageAltText = createSelector(
  getters.styles,
  R.prop("background_image_url_alt_text")
);
export const getPrimaryButtonColor = createSelector(
  getters.styles,
  R.prop("primary_button_color")
);
export const getPrimaryButtonTextColor = createSelector(
  getters.styles,
  R.prop("primary_button_text_color")
);
export const getSecondaryButtonColor = createSelector(
  getters.styles,
  R.prop("secondary_button_color")
);
export const getSecondaryButtonTextColor = createSelector(
  getters.styles,
  R.prop("secondary_button_text_color")
);

export const getBackgroundColor = createSelector(
  getters.styles,
  R.prop("background_color")
);

// typography
export const getWelcomeMessageFont = createSelector(
  getters.styles,
  R.compose(getFontCssName, R.prop("welcome_message_font"))
);
export const getWelcomeMessageSize = createSelector(
  getters.styles,
  R.compose(size => (size ? `${size}px` : null), R.prop("welcome_message_size"))
);
export const getWelcomeMessageColor = createSelector(
  getters.styles,
  R.prop("welcome_message_color")
);
export const getWelcomeMessageText = createSelector(
  getters.styles,
  R.prop("welcome_message_text")
);

export const getSystemFont = createSelector(
  getters.styles,
  R.compose(getFontCssName, R.prop("system_font"))
);
export const getFormTitleSize = createSelector(
  getters.styles,
  R.compose(size => (size ? `${size}px` : null), R.prop("form_title_font_size"))
);
export const getFormTitleColor = createSelector(
  getters.styles,
  R.prop("form_title_font_color")
);

export const getSectionHeaderSize = createSelector(
  getters.styles,
  R.compose(
    size => (size ? `${size}px` : null),
    R.prop("section_header_font_size")
  )
);
export const getSectionHeaderColor = createSelector(
  getters.styles,
  R.prop("section_header_font_color")
);
export const getSectionDescriptionSize = createSelector(
  getters.styles,
  R.compose(
    size => (size ? `${size}px` : null),
    R.prop("section_description_font_size")
  )
);
export const getSectionDescriptionColor = createSelector(
  getters.styles,
  R.prop("section_description_font_color")
);

export const getQuestionLabelSize = createSelector(
  getters.styles,
  R.compose(
    size => (size ? `${size}px` : null),
    R.prop("question_labels_font_size")
  )
);
export const getQuestionLabelColor = createSelector(
  getters.styles,
  R.prop("question_labels_font_color")
);

export const getHeaderCustomHtml = createSelector(
  getters.styles,
  R.prop("header_custom_html")
);

export const getFooterCustomHtml = createSelector(
  getters.styles,
  R.prop("footer_custom_html")
);

export const getJsCode = createSelector(getters.styles, R.prop("js_code"));

export const getCssCode = createSelector(
  getters.styles,
  getBackgroundColor,
  getSystemFont,
  (styles, backgroundColor, systemFont) => {
    let css = "";

    const fonts = R.compose(
      R.uniq,
      R.filter(f => f)
    )([styles.welcome_message_font, styles.system_font]);

    const fontCss = resolveFontCss({ fonts });
    if (fontCss && fontCss.length) {
      css = `${fontCss}`;
    }

    // @NOTE: We have to add background color this way because the higher layer is outside the core route
    if (backgroundColor) {
      css = `${css}

body {
  background-color: ${backgroundColor};
  font-family: ${systemFont};
}
p, div, span, h1, h2, h3, button, input, textarea {
  font-family: ${systemFont};
}
`;
    }

    if (styles.css_code && styles.css_code.length) {
      css = `${css}

${styles.css_code}`;
    }

    return css;
  }
);
